// File: src/components/Admin/BrandManagement.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  InputAdornment,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  CloudUpload as UploadIcon,
  Language as DomainIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
// Color input
import api from '../../services/api';

interface Brand {
  id: number;
  domain: string;
  brand_name: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  login_background_url?: string;
  custom_css?: string;
  metadata?: any;
  is_active: boolean;
  created_at: string;
  // Email configuration fields
  email_provider?: string;
  from_email?: string;
  mailtrap_api_key?: string;
  mailtrap_inbox_id?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  smtp_encryption?: string;
  support_email?: string;
  support_phone?: string;
  billing_email?: string;
  sales_email?: string;
  notifications_email?: string;
}

interface BrandFormData {
  domain: string;
  brand_name: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  login_background_url: string;
  custom_css: string;
  is_active: boolean;
  // Email configuration fields
  email_provider: string;
  from_email: string;
  mailtrap_api_key: string;
  mailtrap_inbox_id: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_encryption: string;
  support_email: string;
  support_phone: string;
  billing_email: string;
  sales_email: string;
  notifications_email: string;
}

const BrandManagement: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [formData, setFormData] = useState<BrandFormData>({
    domain: '',
    brand_name: '',
    logo_url: '/OBPLogo1.png',
    favicon_url: '/favicon.ico',
    primary_color: '#1976d2',
    secondary_color: '#dc004e',
    login_background_url: '',
    custom_css: '',
    is_active: true,
    // Email configuration defaults
    email_provider: 'default',
    from_email: '',
    mailtrap_api_key: '',
    mailtrap_inbox_id: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    support_email: '',
    support_phone: '',
    billing_email: '',
    sales_email: '',
    notifications_email: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tempFiles, setTempFiles] = useState<{ [key: string]: File }>({});
  const [, forceUpdate] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchBrands();
  }, []);

  // Debug effect to log state changes
  useEffect(() => {
    console.log('Form data changed:', formData);
    console.log('Current logo_url value:', formData.logo_url);
  }, [formData]);

  useEffect(() => {
    console.log('Temp files changed:', tempFiles);
  }, [tempFiles]);

  useEffect(() => {
    document.title = 'Brand Management - Portal';
  }, []);

  // Test effect to ensure component is mounting
  useEffect(() => {
    console.log('BrandManagement component mounted');
    console.log('Initial state:', { formData, tempFiles, dialogOpen, tabValue });
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      console.log('Fetching brands...');
      const response = await api.get('/endpoints/branding');
      console.log('Brands response:', response);
      if (response.data.success) {
        console.log('Setting brands:', response.data.data);
        // Log the logo URLs to debug
        response.data.data.forEach((brand: Brand) => {
          console.log(`Brand ${brand.id} (${brand.brand_name}) logo_url:`, brand.logo_url);
        });
        setBrands(response.data.data);
      } else {
        console.error('API returned success=false:', response.data);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to load brands';
      setError(errorMsg);
      console.error('Error fetching brands:', err);
      console.error('Error response:', err.response);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedBrand(null);
    setFormData({
      domain: '',
      brand_name: '',
      logo_url: '/OBPLogo1.png',
      favicon_url: '/favicon.ico',
      primary_color: '#1976d2',
      secondary_color: '#dc004e',
      login_background_url: '',
      custom_css: '',
      is_active: true,
      // Email configuration defaults
      email_provider: 'default',
      from_email: '',
      mailtrap_api_key: '',
      mailtrap_inbox_id: '',
      smtp_host: '',
      smtp_port: 587,
      smtp_username: '',
      smtp_password: '',
      smtp_encryption: 'tls',
      support_email: '',
      support_phone: '',
      billing_email: '',
      sales_email: '',
      notifications_email: '',
    });
    setTempFiles({});
    setDialogOpen(true);
    setTabValue(0);
  };

  const handleEdit = (brand: Brand) => {
    setSelectedBrand(brand);
    
    setFormData({
      domain: brand.domain,
      brand_name: brand.brand_name,
      logo_url: brand.logo_url || '',
      favicon_url: brand.favicon_url || '',
      primary_color: brand.primary_color,
      secondary_color: brand.secondary_color,
      login_background_url: brand.login_background_url || '',
      custom_css: brand.custom_css || '',
      is_active: brand.is_active,
      // Email configuration fields
      email_provider: brand.email_provider || 'default',
      from_email: brand.from_email || '',
      mailtrap_api_key: brand.mailtrap_api_key || '',
      mailtrap_inbox_id: brand.mailtrap_inbox_id || '',
      smtp_host: brand.smtp_host || '',
      smtp_port: brand.smtp_port || 587,
      smtp_username: brand.smtp_username || '',
      smtp_password: brand.smtp_password || '',
      smtp_encryption: brand.smtp_encryption || 'tls',
      support_email: brand.support_email || '',
      support_phone: brand.support_phone || '',
      billing_email: brand.billing_email || '',
      sales_email: brand.sales_email || '',
      notifications_email: brand.notifications_email || '',
    });
    setTempFiles({});
    setDialogOpen(true);
    setTabValue(0);
  };

  const handleDelete = (brand: Brand) => {
    setSelectedBrand(brand);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      console.log('Saving brand...', { selectedBrand, formData });
      console.log('Current state - tempFiles:', tempFiles);
      console.log('Current logo_url value:', formData.logo_url);
      console.log('Dialog open?', dialogOpen);
      console.log('Tab value:', tabValue);
      
      // Clean up the form data
      const cleanedFormData = { ...formData };
      
      // If the URL looks like a filename or a localhost URL, don't save it
      ['logo_url', 'favicon_url', 'login_background_url'].forEach(field => {
        const value = cleanedFormData[field as keyof typeof cleanedFormData] as string;
        if (value && (
          value.includes('localhost:') || 
          value.startsWith('http://localhost') ||
          (!value.startsWith('/') && !value.startsWith('http'))
        )) {
          // This is likely a filename or localhost URL, use default instead
          if (field === 'logo_url') cleanedFormData.logo_url = '/OBPLogo1.png';
          else if (field === 'favicon_url') cleanedFormData.favicon_url = '/favicon.ico';
          else cleanedFormData.login_background_url = '';
        }
      });
      
      if (selectedBrand) {
        // Update existing brand
        console.log('Updating brand:', selectedBrand.id);
        const response = await api.put(`/endpoints/branding/${selectedBrand.id}`, cleanedFormData);
        console.log('Update response:', response);
        setSuccess('Brand updated successfully');
      } else {
        // Create new brand
        console.log('Creating new brand');
        const response = await api.post('/endpoints/branding', cleanedFormData);
        console.log('Create response:', response);
        
        // If we have temp files and brand was created successfully, upload them
        if (response.data.success && response.data.data?.id && Object.keys(tempFiles).length > 0) {
          const brandId = response.data.data.id;
          
          // Upload each temp file
          for (const [field, file] of Object.entries(tempFiles)) {
            const uploadData = new FormData();
            const fieldMap: { [key: string]: string } = {
              'logo_url': 'logo',
              'favicon_url': 'favicon',
              'login_background_url': 'login_background'
            };
            
            uploadData.append(fieldMap[field], file);
            
            try {
              await api.post(`/endpoints/branding-upload?brand_id=${brandId}`, uploadData, {
                headers: {
                  'Content-Type': 'multipart/form-data',
                },
              });
            } catch (uploadErr) {
              console.error(`Failed to upload ${field}:`, uploadErr);
            }
          }
          
          // Clear temp files
          setTempFiles({});
        }
        
        setSuccess('Brand created successfully');
      }
      setDialogOpen(false);
      fetchBrands();
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || 'Failed to save brand');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBrand) return;

    try {
      await api.delete(`/endpoints/branding/${selectedBrand.id}`);
      setSuccess('Brand deleted successfully');
      setDeleteDialogOpen(false);
      fetchBrands();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete brand');
    }
  };

  const handlePreview = (brand: Brand) => {
    setSelectedBrand(brand);
    setPreviewOpen(true);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    alert(`handleFileUpload called for field: ${field}`);
    console.log('handleFileUpload called for field:', field);
    const file = event.target.files?.[0];
    console.log('Selected file:', file);
    if (!file) {
      console.log('No file selected');
      return;
    }

    try {
      // Show loading state
      setError(null);
      console.log('Starting file upload...');
      
      // Create form data
      const uploadData = new FormData();
      
      // Map field names to the expected upload names
      const fieldMap: { [key: string]: string } = {
        'logo_url': 'logo',
        'favicon_url': 'favicon',
        'login_background_url': 'login_background'
      };
      
      // Debug file details BEFORE appending
      console.log('=== FILE UPLOAD DEBUG ===');
      console.log('1. File object exists?', file !== null && file !== undefined);
      console.log('2. File is File instance?', file instanceof File);
      console.log('3. File details:');
      console.log('   - name:', file.name);
      console.log('   - size:', file.size);
      console.log('   - type:', file.type);
      console.log('   - lastModified:', file.lastModified);
      console.log('4. Field mapping:');
      console.log('   - Original field:', field);
      console.log('   - Mapped to:', fieldMap[field]);
      
      uploadData.append(fieldMap[field], file);
      
      // Verify the append worked
      console.log('5. After append - FormData has entry?', uploadData.has(fieldMap[field]));
      
      // Try to get the value back
      const appendedValue = uploadData.get(fieldMap[field]);
      console.log('6. Retrieved value:', appendedValue);
      console.log('7. Retrieved value is File?', appendedValue instanceof File);
      
      // Try a simple test append
      const testData = new FormData();
      testData.append('test', 'hello');
      testData.append('testfile', file);
      console.log('8. Test FormData has test?', testData.has('test'));
      console.log('9. Test FormData has testfile?', testData.has('testfile'));
      
      // If we have a selected brand (editing), upload to the existing brand
      if (selectedBrand) {
        console.log('Uploading to existing brand:', selectedBrand.id);
        console.log('Current formData:', formData);
        // First, update the UI to show the file is being uploaded
        const updatedFormData = { ...formData, [field]: `Uploading ${file.name}...` };
        console.log('Updated formData:', updatedFormData);
        setFormData(updatedFormData);
        
        console.log('Sending request to:', `/endpoints/branding-upload?brand_id=${selectedBrand.id}`);
        
        // Log the token being used
        const token = localStorage.getItem('access_token');
        console.log('Token being used:', token ? `${token.substring(0, 20)}...` : 'No token found');
        
        // Debug: Check uploadData is FormData instance
        console.log('uploadData is FormData?', uploadData instanceof FormData);
        console.log('uploadData constructor:', uploadData.constructor.name);
        
        // Create a new FormData to ensure it's clean
        const finalFormData = new FormData();
        finalFormData.append(fieldMap[field], file);
        
        console.log('Sending FormData with field:', fieldMap[field]);
        
        // Test with raw fetch to bypass axios
        console.log('=== RAW FETCH TEST ===');
        console.log('1. Testing with raw fetch API...');
        console.log('2. FormData before send:', finalFormData);
        console.log('3. FormData has logo?', finalFormData.has('logo'));
        console.log('4. FormData has favicon?', finalFormData.has('favicon'));
        console.log('5. FormData has login_background?', finalFormData.has('login_background'));
        
        try {
          // Use direct URL for local development to bypass proxy issues
          const apiUrl = window.location.hostname === 'localhost' 
            ? `http://localhost:8080/api/v1/endpoints/branding-upload?brand_id=${selectedBrand.id}`
            : `/api/v1/endpoints/branding-upload?brand_id=${selectedBrand.id}`;
          
          const testResponse = await fetch(apiUrl, {
            method: 'POST',
            body: finalFormData,
            headers: {
              'Authorization': `Bearer ${token}`
              // Let browser set Content-Type with boundary
            }
          });
          
          console.log('6. Response status:', testResponse.status);
          console.log('7. Response headers:', testResponse.headers);
          
          const responseText = await testResponse.text();
          console.log('8. Raw response:', responseText);
          
          let testResult;
          try {
            testResult = JSON.parse(responseText);
          } catch (e) {
            console.error('9. Failed to parse response as JSON');
            testResult = { success: false, message: responseText };
          }
          
          console.log('10. Parsed result:', testResult);
          
          if (testResult.success) {
            // Update form with the filename
            setFormData(prev => ({ ...prev, [field]: file.name }));
            setSuccess(`File uploaded successfully: ${file.name}`);
            forceUpdate(n => n + 1);
            setTimeout(() => fetchBrands(), 100);
            return;
          }
        } catch (fetchError) {
          console.error('Raw fetch error:', fetchError);
          setError('Failed to upload file. Please try again.');
        }
      } else {
        // For new brands, we'll store the file object temporarily
        // Store the file in a separate state to upload after brand creation
        setTempFiles({ ...tempFiles, [field]: file });
        setFormData({ ...formData, [field]: file.name });
        setSuccess(`File selected: ${file.name} (will be uploaded when brand is saved)`);
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      console.error('Error details:', err.response);
      setError(err.response?.data?.message || 'Failed to upload file');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Brand Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add Brand
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Brand</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Colors</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">No brands found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              brands.map((brand) => (
              <TableRow key={brand.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={brand.logo_url} alt={brand.brand_name}>
                      {brand.brand_name[0]}
                    </Avatar>
                    <Typography>{brand.brand_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <DomainIcon fontSize="small" color="action" />
                    <Typography variant="body2">{brand.domain}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <Tooltip title="Primary Color">
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: brand.primary_color,
                          border: '1px solid #ddd',
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Secondary Color">
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: brand.secondary_color,
                          border: '1px solid #ddd',
                        }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={brand.is_active ? 'Active' : 'Inactive'}
                    color={brand.is_active ? 'success' : 'default'}
                    size="small"
                    icon={brand.is_active ? <CheckIcon /> : <CloseIcon />}
                  />
                </TableCell>
                <TableCell>
                  {new Date(brand.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Preview">
                    <IconButton onClick={() => handlePreview(brand)} size="small">
                      <PreviewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(brand)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <span>
                      <IconButton 
                        onClick={() => handleDelete(brand)} 
                        size="small"
                        disabled={['localhost:3000', 'pbx4.iatelecom.ca', 'pbx4.ourbizphone.com'].includes(brand.domain)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedBrand ? 'Edit Brand' : 'Add New Brand'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Basic Info" />
            <Tab label="Assets" />
            <Tab label="Email Config" />
            <Tab label="Advanced" />
          </Tabs>

          {tabValue === 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Domain"
                  fullWidth
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="example.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DomainIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Brand Name"
                  fullWidth
                  value={formData.brand_name}
                  onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                  placeholder="My Company"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Primary Color"
                  type="color"
                  fullWidth
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { height: 40 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Secondary Color"
                  type="color"
                  fullWidth
                  value={formData.secondary_color}
                  onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { height: 40 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Click the button below to select a file. Check console for debug messages.
                </Alert>
                <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc' }}>
                  <Typography variant="body2">Test: Basic file input test</Typography>
                  <input
                    type="file"
                    onChange={(e) => {
                      alert('File input changed! Check console.');
                      console.log('File selected:', e.target.files?.[0]?.name || 'No file');
                    }}
                  />
                  <br /><br />
                  <Button 
                    variant="contained" 
                    onClick={() => {
                      alert('Button clicked!');
                      console.log('Test button clicked at', new Date().toISOString());
                    }}
                  >
                    Test Button Click
                  </Button>
                  <br /><br />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      console.log('Attempting to trigger file input programmatically');
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e: any) => {
                        alert('File selected via programmatic input!');
                        console.log('File:', e.target.files?.[0]);
                      };
                      input.click();
                    }}
                  >
                    Test Programmatic File Input
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  key={`logo-${formData.logo_url}-${tempFiles['logo_url']?.name || ''}`}
                  label="Logo"
                  fullWidth
                  value={tempFiles['logo_url'] ? tempFiles['logo_url'].name : formData.logo_url}
                  InputProps={{
                    readOnly: true,
                  }}
                  placeholder="No logo uploaded"
                  helperText="Upload a logo image (PNG, JPG, or SVG)"
                />
                <input
                  type="file"
                  id="logo-file-input"
                  style={{ display: 'none' }}
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={(e) => {
                    console.log('File input onChange triggered');
                    handleFileUpload(e, 'logo_url');
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 1 }}
                  fullWidth
                  onClick={() => {
                    console.log('Upload button clicked');
                    const input = document.getElementById('logo-file-input') as HTMLInputElement;
                    if (input) {
                      console.log('Triggering file input click');
                      input.click();
                    } else {
                      console.error('File input not found');
                    }
                  }}
                >
                  {tempFiles['logo_url'] ? 'Change Logo' : 'Select Logo'}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  key={`favicon-${formData.favicon_url}-${tempFiles['favicon_url']?.name || ''}`}
                  label="Favicon"
                  fullWidth
                  value={tempFiles['favicon_url'] ? tempFiles['favicon_url'].name : formData.favicon_url}
                  InputProps={{
                    readOnly: true,
                  }}
                  placeholder="No favicon uploaded"
                  helperText="Upload a favicon (ICO or PNG)"
                />
                <input
                  type="file"
                  id="favicon-file-input"
                  style={{ display: 'none' }}
                  accept="image/x-icon,image/vnd.microsoft.icon,image/png"
                  onChange={(e) => {
                    console.log('Favicon input onChange triggered');
                    handleFileUpload(e, 'favicon_url');
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 1 }}
                  fullWidth
                  onClick={() => {
                    console.log('Favicon button clicked');
                    const input = document.getElementById('favicon-file-input') as HTMLInputElement;
                    if (input) input.click();
                  }}
                >
                  {tempFiles['favicon_url'] ? 'Change Favicon' : 'Select Favicon'}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  key={`bg-${formData.login_background_url}-${tempFiles['login_background_url']?.name || ''}`}
                  label="Login Background"
                  fullWidth
                  value={tempFiles['login_background_url'] ? tempFiles['login_background_url'].name : formData.login_background_url}
                  InputProps={{
                    readOnly: true,
                  }}
                  placeholder="No background uploaded"
                  helperText="Upload a background image for the login page"
                />
                <input
                  type="file"
                  id="background-file-input"
                  style={{ display: 'none' }}
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={(e) => {
                    console.log('Background input onChange triggered');
                    handleFileUpload(e, 'login_background_url');
                  }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 1 }}
                  fullWidth
                  onClick={() => {
                    console.log('Background button clicked');
                    const input = document.getElementById('background-file-input') as HTMLInputElement;
                    if (input) input.click();
                  }}
                >
                  {tempFiles['login_background_url'] ? 'Change Background' : 'Select Background'}
                </Button>
              </Grid>
            </Grid>
          )}

          {/* Email Configuration Tab */}
          {tabValue === 2 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Email Provider</InputLabel>
                  <Select
                    value={formData.email_provider}
                    label="Email Provider"
                    onChange={(e) => setFormData({ ...formData, email_provider: e.target.value })}
                  >
                    <MenuItem value="default">Default (IA Telecom)</MenuItem>
                    <MenuItem value="mailtrap">Mailtrap</MenuItem>
                    <MenuItem value="smtp">SMTP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="From Email"
                  fullWidth
                  value={formData.from_email}
                  onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                  placeholder="billing@example.com"
                  helperText="Email address used as sender"
                />
              </Grid>
              
              {/* Mailtrap Settings */}
              {formData.email_provider === 'mailtrap' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Mailtrap API Key"
                      fullWidth
                      type="password"
                      value={formData.mailtrap_api_key}
                      onChange={(e) => setFormData({ ...formData, mailtrap_api_key: e.target.value })}
                      placeholder="Enter Mailtrap API key"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Mailtrap Inbox ID (Optional)"
                      fullWidth
                      value={formData.mailtrap_inbox_id}
                      onChange={(e) => setFormData({ ...formData, mailtrap_inbox_id: e.target.value })}
                      placeholder="Inbox ID"
                    />
                  </Grid>
                </>
              )}
              
              {/* SMTP Settings */}
              {formData.email_provider === 'smtp' && (
                <>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="SMTP Host"
                      fullWidth
                      value={formData.smtp_host}
                      onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                      placeholder="mail.example.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="SMTP Port"
                      fullWidth
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) || 587 })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="SMTP Username"
                      fullWidth
                      value={formData.smtp_username}
                      onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                      placeholder="username@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="SMTP Password"
                      fullWidth
                      type="password"
                      value={formData.smtp_password}
                      onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                      placeholder="Enter password"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Encryption</InputLabel>
                      <Select
                        value={formData.smtp_encryption}
                        label="Encryption"
                        onChange={(e) => setFormData({ ...formData, smtp_encryption: e.target.value })}
                      >
                        <MenuItem value="tls">TLS</MenuItem>
                        <MenuItem value="ssl">SSL</MenuItem>
                        <MenuItem value="none">None</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
              
              {/* Department Email Addresses */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Department Email Addresses</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Billing Email"
                  fullWidth
                  value={formData.billing_email}
                  onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                  placeholder="billing@example.com"
                  helperText="Email for billing and invoice communications"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Sales Email"
                  fullWidth
                  value={formData.sales_email}
                  onChange={(e) => setFormData({ ...formData, sales_email: e.target.value })}
                  placeholder="sales@example.com"
                  helperText="Email for sales inquiries"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Notifications Email"
                  fullWidth
                  value={formData.notifications_email}
                  onChange={(e) => setFormData({ ...formData, notifications_email: e.target.value })}
                  placeholder="no-reply@example.com"
                  helperText="Email for system notifications (password resets, etc.)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Support Email"
                  fullWidth
                  value={formData.support_email}
                  onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                  placeholder="support@example.com"
                  helperText="Email for customer support"
                />
              </Grid>
              
              {/* Support Phone */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>Contact Information</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Support Phone"
                  fullWidth
                  value={formData.support_phone}
                  onChange={(e) => setFormData({ ...formData, support_phone: e.target.value })}
                  placeholder="1-888-555-0000"
                  helperText="Support phone number shown to users"
                />
              </Grid>
            </Grid>
          )}

          {/* Advanced Tab (was tabValue === 2, now === 3) */}
          {tabValue === 3 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Custom CSS"
                  fullWidth
                  multiline
                  rows={10}
                  value={formData.custom_css}
                  onChange={(e) => setFormData({ ...formData, custom_css: e.target.value })}
                  placeholder="/* Custom styles */"
                  sx={{ fontFamily: 'monospace' }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {selectedBrand ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Brand?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the brand "{selectedBrand?.brand_name}" 
            for domain "{selectedBrand?.domain}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Brand Preview: {selectedBrand?.brand_name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardMedia
                  component="img"
                  height="140"
                  image={selectedBrand?.logo_url}
                  alt="Logo"
                  sx={{ objectFit: 'contain', p: 2 }}
                />
                <CardContent>
                  <Typography gutterBottom variant="h6">
                    Logo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedBrand?.logo_url}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <img 
                    src={selectedBrand?.favicon_url} 
                    alt="Favicon" 
                    style={{ width: 64, height: 64 }}
                  />
                </Box>
                <CardContent>
                  <Typography gutterBottom variant="h6">
                    Favicon
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedBrand?.favicon_url}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Color Scheme
                </Typography>
                <Box display="flex" gap={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Primary
                    </Typography>
                    <Box
                      sx={{
                        width: 100,
                        height: 50,
                        backgroundColor: selectedBrand?.primary_color,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption">
                      {selectedBrand?.primary_color}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Secondary
                    </Typography>
                    <Box
                      sx={{
                        width: 100,
                        height: 50,
                        backgroundColor: selectedBrand?.secondary_color,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant="caption">
                      {selectedBrand?.secondary_color}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandManagement;